import type { ReactNode, RefObject } from "react";
import {
  ArrowRightIcon,
  CalendarClockIcon,
  HistoryIcon,
  PhoneOffIcon,
} from "lucide-react";

import { VoiceAiCallsList } from "@/components/user-sheet/records/VoiceAiCallsList";

import {
  Center,
  Column,
  EmptyState,
  ErrorText,
  Row,
  SheetSectionHeader,
  Text,
} from "@/components";
import { Button } from "@/components/ui/button";

import { useUserSheet } from "@/components/user-sheet/userSheetContext";

import { dates } from "@/lib/dates";
import { CallHistoryAiCall, VoiceAIOutreachStatus } from "@/types";

// Only completed screenings count here. Voicemails and no-answer attempts
// stay on the Records tab.
const isCompletedCall = (call: CallHistoryAiCall) =>
  call.status === VoiceAIOutreachStatus.COMPLETED.toString();

// A booked call that hasn't been placed yet. Not counted, but worth showing in
// place of "no calls yet" so the section doesn't read as nothing happening.
const isScheduledCall = (call: CallHistoryAiCall) =>
  call.status === VoiceAIOutreachStatus.PENDING.toString();

export const CandidacyStevieCallSection = ({
  aiCalls: allAiCalls,
  audioRef,
  audioRefCallId,
  candidateFirstName,
  currentCallDetails,
  headerActions,
  isError,
  onOpenSheetTab,
  onViewOtherCalls,
  showPastCalls = false,
  titleActions,
}: {
  // The candidate's full AI call history — the tab owns the query (and the
  // single loading gate); this section just renders from it.
  aiCalls: CallHistoryAiCall[];
  // Shared with the breakdown so a citation can seek this call's player.
  audioRef?: RefObject<HTMLAudioElement>;
  // Which call that ref belongs to; every other card keeps its own.
  audioRefCallId?: string;
  candidateFirstName: string;
  // The per-field breakdown, rendered under this candidacy's call.
  currentCallDetails?: ReactNode;
  // Pushed to the right of the header row, opposite the title.
  headerActions?: ReactNode;
  isError: boolean;
  onOpenSheetTab?: (tab: "records") => void;
  // Where the "other Stevie calls" link goes. Falls back to the Records tab
  // for hosts that don't own a dedicated Stevie tab.
  onViewOtherCalls?: () => void;
  // Folds the candidate's other jobs' calls in below a divider.
  showPastCalls?: boolean;
  // Sits inline after the title, in place of the call count.
  titleActions?: ReactNode;
}) => {
  /* Context */
  const { candidacyId } = useUserSheet();

  /* Helpers */
  const aiCalls = allAiCalls.filter(isCompletedCall);
  // A candidacy usually has one completed Stevie call, but re-screens can
  // produce several. Render them all rather than showing only the newest.
  const candidacyStevieCalls = aiCalls.filter(
    (call) => call.candidacyId === candidacyId,
  );
  const pastStevieCalls = aiCalls.filter(
    (call) => call.candidacyId !== candidacyId,
  );
  const otherCandidacyCallsCount = pastStevieCalls.length;
  const scheduledCall =
    allAiCalls.find(
      (call) => call.candidacyId === candidacyId && isScheduledCall(call),
    ) ?? null;

  /* Render */
  // The Stevie tab passes its Cancel call button as headerActions, so the
  // control is already in view there. Elsewhere, link to where it lives:
  // the Stevie tab on pilot jobs, the Records tab otherwise.
  const canLinkToCall =
    !headerActions && !!(onViewOtherCalls ?? onOpenSheetTab);

  const openCallControls = () => {
    if (onViewOtherCalls) {
      onViewOtherCalls();
      return;
    }
    onOpenSheetTab?.("records");
  };

  const formatScheduledFor = (call: CallHistoryAiCall) =>
    dates.formatIsoDateTimeForUser(call.scheduledDateTime) ??
    "an upcoming time";

  const renderCallControlsLink = (label: string) => {
    if (!canLinkToCall) return null;
    return (
      <Button
        className="group h-auto gap-1 p-0 text-sm font-normal text-primary-900 hover:bg-transparent"
        onClick={openCallControls}
        type="button"
        variant="link"
      >
        <Text className="text-sm font-normal text-primary-900">{label}</Text>
        <ArrowRightIcon className="h-3.5 w-3.5 -translate-x-1 opacity-0 transition-all group-hover:translate-x-0 group-hover:opacity-100" />
      </Button>
    );
  };

  // Nothing completed yet: the scheduled call is the whole story.
  const renderScheduled = (call: CallHistoryAiCall) => {
    return (
      <Column className="h-48 items-center justify-center gap-4 rounded-xl border border-dashed border-primary-200 bg-primary-50/40 py-8">
        <Center className="rounded-full p-4">
          <CalendarClockIcon className="h-8 w-8 text-slate-600" />
        </Center>
        <Column className="items-center gap-2">
          <Text className="text-base font-semibold text-slate-600">
            Stevie call scheduled
          </Text>
          <Text className="text-center text-sm text-slate-500">
            {`Stevie will call ${candidateFirstName} on ${formatScheduledFor(call)}.`}
          </Text>
          {renderCallControlsLink("Cancel or view call details")}
        </Column>
      </Column>
    );
  };

  // A re-screen booked on top of a completed call: a slim banner above the
  // call cards, since the list itself only shows completed screenings.
  const renderScheduledBanner = (call: CallHistoryAiCall) => {
    return (
      <Row className="items-center justify-between gap-3 rounded-xl border border-dashed border-primary-200 bg-primary-50/40 px-4 py-3">
        <Row className="min-w-0 items-center gap-3">
          <CalendarClockIcon className="h-5 w-5 shrink-0 text-slate-600" />
          <Text className="text-sm text-slate-600">
            {`Re-screen scheduled: Stevie will call ${candidateFirstName} on ${formatScheduledFor(call)}.`}
          </Text>
        </Row>
        {renderCallControlsLink("Cancel or view")}
      </Row>
    );
  };

  const renderNeverCalled = () => {
    return (
      <EmptyState
        className="h-48 rounded-xl border border-dashed border-slate-200 bg-slate-50/50 py-8"
        description={`${candidateFirstName} hasn't completed a Stevie call yet.`}
        descriptionClassName="text-center text-sm"
        headerClassName="text-base"
        Icon={PhoneOffIcon}
        iconContainerClassName="bg-transparent"
        title="No Stevie calls yet"
      />
    );
  };

  const renderOtherCandidacy = () => {
    const linkLabel =
      otherCandidacyCallsCount === 1
        ? "One other Stevie call"
        : `${String(otherCandidacyCallsCount)} other Stevie calls`;

    return (
      <Column className="h-48 items-center justify-center gap-4 rounded-xl border border-dashed border-slate-200 bg-slate-50/50 py-8">
        <Center className="rounded-full p-4">
          <HistoryIcon className="h-8 w-8 text-slate-600" />
        </Center>
        <Column className="items-center gap-2">
          <Text className="text-base font-semibold text-slate-600">
            No call for this candidacy
          </Text>
          <Button
            className="group h-auto gap-1 p-0 text-sm font-normal text-primary-900 hover:bg-transparent"
            onClick={() => {
              if (onViewOtherCalls) {
                onViewOtherCalls();
                return;
              }
              onOpenSheetTab?.("records");
            }}
            type="button"
            variant="link"
          >
            <Text className="text-sm font-normal text-primary-900">
              {linkLabel}
            </Text>
            <ArrowRightIcon className="h-3.5 w-3.5 -translate-x-1 opacity-0 transition-all group-hover:translate-x-0 group-hover:opacity-100" />
          </Button>
        </Column>
      </Column>
    );
  };

  const renderContent = () => {
    if (isError) {
      return <ErrorText errorMessage="Error retrieving calls." />;
    }
    if (candidacyStevieCalls.length === 0 && scheduledCall) {
      // With past calls on show, the scheduled notice sits above them.
      if (showPastCalls && aiCalls.length > 0) {
        return (
          <Column className="gap-4">
            {renderScheduled(scheduledCall)}
            <VoiceAiCallsList
              calls={pastStevieCalls}
              candidateFirstName={candidateFirstName}
              collapsePastCalls
              currentCandidacyId={candidacyId ?? undefined}
              hideCallIdentity
              hideCurrentJobEmptyNote
              isError={false}
              isPending={false}
            />
          </Column>
        );
      }
      return renderScheduled(scheduledCall);
    }
    if (
      candidacyStevieCalls.length > 0 ||
      (showPastCalls && aiCalls.length > 0)
    ) {
      const list = (
        <VoiceAiCallsList
          audioRef={audioRef}
          audioRefCallId={audioRefCallId}
          calls={
            showPastCalls
              ? [...candidacyStevieCalls, ...pastStevieCalls]
              : candidacyStevieCalls
          }
          candidateFirstName={candidateFirstName}
          collapsePastCalls={showPastCalls}
          currentCallDetails={currentCallDetails}
          currentCandidacyId={candidacyId ?? undefined}
          // The section heading and the surrounding candidacy already say
          // whose Stevie call this is! The card keeps just the date, player,
          // and detail tabs.
          hideCallIdentity
          isError={false}
          isPending={false}
        />
      );
      if (!scheduledCall) return list;
      return (
        <Column className="gap-4">
          {renderScheduledBanner(scheduledCall)}
          {list}
        </Column>
      );
    }
    if (aiCalls.length > 0) return renderOtherCandidacy();
    return renderNeverCalled();
  };

  const render = () => {
    return (
      <Column className="gap-4">
        <Row className="items-center justify-between gap-2">
          <Row className="items-center gap-2">
            <SheetSectionHeader title="Stevie" />
            {titleActions ?? (
              <Text className="shrink-0 rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600">
                {candidacyStevieCalls.length}
              </Text>
            )}
          </Row>
          {headerActions}
        </Row>
        {renderContent()}
      </Column>
    );
  };

  return render();
};
